import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, HelpCircle } from 'lucide-react';

const GuidedTour = ({ steps, isActive, onClose, onComplete, currentView, onViewChange, onStepChange, singleStepMode, stepIndex = 0, totalSteps = 1, stepLabel = 'Tip' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState({});
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const pendingViewChangeRef = useRef(null);
  const onBeforeStepTimeoutRef = useRef(null);
  
  const effectiveStepIndex = singleStepMode ? stepIndex : currentStep;
  const effectiveTotalSteps = singleStepMode ? totalSteps : (steps?.length || 1);
  
  // Notify parent component when step changes
  useEffect(() => {
    if (onStepChange && isActive) {
      onStepChange(singleStepMode ? stepIndex : currentStep);
    }
  }, [currentStep, isActive, onStepChange, singleStepMode, stepIndex]);

  // Reset to first step when tour starts
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
      setShowTooltip(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !steps || steps.length === 0) return;

    const step = steps[currentStep];
    if (!step) return;

    // Check if we need to change view for this step
    if (step.requiredView && currentView !== step.requiredView && onViewChange) {
      if (pendingViewChangeRef.current !== step.requiredView) {
        pendingViewChangeRef.current = step.requiredView;
        onViewChange(step.requiredView);
      }
      setHighlightStyle({});
      setTooltipPosition({
        top: Math.max(20, window.innerHeight / 2 - 100),
        left: Math.max(20, window.innerWidth / 2 - 160)
      });
      setShowTooltip(true);
      return;
    }
    
    if (step.requiredView && currentView === step.requiredView) {
      pendingViewChangeRef.current = null;
    }
    
    if (step.onBeforeStep && typeof step.onBeforeStep === 'function') {
      if (onBeforeStepTimeoutRef.current) {
        clearTimeout(onBeforeStepTimeoutRef.current);
      }
      const delay = step.requiredView ? 50 : 0;
      onBeforeStepTimeoutRef.current = setTimeout(() => {
        step.onBeforeStep();
        onBeforeStepTimeoutRef.current = null;
      }, delay);
    }

    if (!step.target) {
      setHighlightStyle({});
      setTooltipPosition({ top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 160 });
      setShowTooltip(true);
      return;
    }

    let retryCount = 0;
    const maxRetries = 8;

    const updatePosition = () => {
      if (step.requiredView && currentView !== step.requiredView) {
        return;
      }

      const element = document.querySelector(step.target);
      if (!element) {
        retryCount += 1;
        if (retryCount < maxRetries) {
          requestAnimationFrame(() => {
            const retryElement = document.querySelector(step.target);
            if (retryElement) {
              updatePosition();
            } else {
              updatePosition();
            }
          });
        } else {
          setHighlightStyle({});
          setTooltipPosition({
            top: Math.max(20, window.innerHeight / 2 - 100),
            left: Math.max(20, window.innerWidth / 2 - 160)
          });
          setShowTooltip(true);
        }
        return;
      }

      const rect = element.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      setHighlightStyle({
        position: 'absolute',
        top: rect.top + scrollY,
        left: rect.left + scrollX,
        width: rect.width,
        height: rect.height,
        border: '3px solid #6366f1',
        borderRadius: '8px',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.3)',
        zIndex: 9998,
        pointerEvents: 'none',
        transition: 'none',
      });

      const tooltipWidth = 320;
      const tooltipHeight = 200;
      let top = rect.bottom + scrollY + 20;
      let left = rect.left + scrollX + rect.width / 2 - tooltipWidth / 2;

      if (left < 20) left = 20;
      if (left + tooltipWidth > window.innerWidth - 20) {
        left = window.innerWidth - tooltipWidth - 20;
      }

      if (top + tooltipHeight > scrollY + window.innerHeight - 20) {
        top = rect.top + scrollY - tooltipHeight - 20;
        if (top < scrollY + 20) {
          top = scrollY + window.innerHeight / 2 - tooltipHeight / 2;
        }
      }

      setTooltipPosition({ top, left });

      element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
      
      setShowTooltip(true);
    };

    const delay = (step.requiredView && currentView === step.requiredView) ? 100 : 0;
    const timer = delay > 0 
      ? setTimeout(updatePosition, delay)
      : requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      if (delay > 0) {
        clearTimeout(timer);
      } else {
        cancelAnimationFrame(timer);
      }
      if (onBeforeStepTimeoutRef.current) {
        clearTimeout(onBeforeStepTimeoutRef.current);
        onBeforeStepTimeoutRef.current = null;
      }
      setShowTooltip(false);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isActive, currentStep, steps, currentView, onViewChange]);

  if (!isActive || !steps || steps.length === 0) return null;

  const step = steps[currentStep];
  if (!step) return null;

  const isFirst = singleStepMode || currentStep === 0;
  const isLast = singleStepMode || currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9997]"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
        onClick={handleSkip}
      />

      {/* Highlight */}
      {step.target && (
        <div
          style={highlightStyle}
          className="pointer-events-none"
        />
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] bg-zinc-900 border-2 border-indigo-500 rounded-lg shadow-2xl transition-opacity duration-200"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            width: '320px',
            maxWidth: '90vw',
            opacity: showTooltip ? 1 : 0,
          }}
        >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <HelpCircle className="h-5 w-5 text-indigo-400" />
                <h3 className="text-lg font-semibold text-white">
                  {step.title}
                </h3>
              </div>
              <p className="text-xs text-zinc-400">
                {singleStepMode ? `${stepLabel} ${effectiveStepIndex + 1} of ${effectiveTotalSteps}` : `Step ${currentStep + 1} of ${steps.length}`}
              </p>
            </div>
            <button
              onClick={handleSkip}
              className="text-zinc-400 hover:text-white transition-colors"
              aria-label="Close tour"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
            {step.content}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {singleStepMode ? 'Skip' : 'Skip tour'}
            </button>
            <div className="flex items-center gap-2">
              {!singleStepMode && !isFirst && (
                <button
                  onClick={handlePrev}
                  className="px-3 py-1.5 text-sm font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
              )}
              <button
                onClick={singleStepMode ? onComplete : handleNext}
                className="px-4 py-1.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors flex items-center gap-1"
              >
                {singleStepMode ? 'Got it' : isLast ? 'Finish' : 'Next'}
                {!singleStepMode && !isLast && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="h-1 bg-zinc-800">
          <div
            className="h-full bg-indigo-500 transition-all duration-300"
            style={{ width: `${((singleStepMode ? effectiveStepIndex + 1 : currentStep + 1) / (singleStepMode ? effectiveTotalSteps : steps.length)) * 100}%` }}
          />
        </div>
      </div>
      )}
    </>
  );
};

export default GuidedTour;
